/**
 * The Apple Watch layout's playfield (docs/GAME_DESIGN.md § 6, 198×242 pt):
 * a thin vertical stack meter instead of the full well, the sum in large
 * type with the time-left stroke round its border, and the same outcome
 * cards in a compact form. The answer grid / pad sits below (AnswerPanel).
 * The top row has the pause button and a one-tap Choices ⇄ Write switch, so
 * both answer modes are playable on the Watch (the full toggle has no room).
 */
import { memo } from 'react';
import type { GameState, Locale } from '@dropstack/core';
import { Icon } from '../components/Icon.tsx';
import { useT } from '../i18n/index.ts';
import { formatInteger } from '../screens/model/format.ts';
import { levelChangeTone } from './announce.ts';
import { AnswerCard, GameOverCard, LevelBanner } from './Cards.tsx';
import { FittedMath } from './FittedMath.tsx';
import { PauseButton } from './Hud.tsx';
import { GameIcon } from './icons.tsx';
import { otherInputMode, type InputMode } from './inputMode.ts';
import { InstructionLine } from './MathText.tsx';
import { fallingView, isDanger } from './playfield.ts';
import { landingDelayMs, resolutionFrame, stackView, TIMING } from './timeline.ts';

export interface StackMeterProps {
  bricks: number;
  max: number;
  danger: boolean;
}

/** Stack height 0 … wellRows − 1 as a column of little bricks, filling from the bottom. */
export const StackMeter = memo(function StackMeter({ bricks, max, danger }: StackMeterProps) {
  const t = useT();
  return (
    <div
      className={`stack-meter${danger ? ' stack-meter--danger' : ''}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={bricks}
      aria-label={t('game.stackMeterLabel', { bricks, max })}
    >
      {Array.from({ length: max }, (_, i) => (
        <span key={i} className={`stack-meter__cell${i < bricks ? ' stack-meter__cell--on' : ''}`} />
      ))}
    </div>
  );
});

export interface CompactModeSwitchProps {
  /** The answer mode on show. */
  mode: InputMode;
  onChoose(mode: InputMode): void;
  disabled: boolean;
}

/**
 * The Watch's answer-mode switch: one icon button showing the mode it
 * switches to (a pencil to write, the grid for choices), for the rest of the
 * run. Small to look at, with the same 44 pt+ hit area as the pause button.
 */
export function CompactModeSwitch({ mode, onChoose, disabled }: CompactModeSwitchProps) {
  const t = useT();
  const target = otherInputMode(mode);
  const label = t(target === 'hw' ? 'game.modeToWrite' : 'game.modeToChoices');
  return (
    <button type="button" className="compact-mode" onClick={() => onChoose(target)} disabled={disabled} aria-label={label} title={label}>
      {target === 'hw' ? <Icon name="pencil" /> : <GameIcon name="choices" />}
    </button>
  );
}

export interface CompactPlayfieldProps {
  state: GameState;
  now: number;
  locale: Locale;
  score: number;
  level: number;
  onPause(): void;
  canPause: boolean;
  /** The answer mode on show, and the switch to change it (the full layout's toggle doesn't fit). */
  mode: InputMode;
  onChooseMode(mode: InputMode): void;
}

export function CompactPlayfield({ state, now, locale, score, level, onPause, canPause, mode, onChooseMode }: CompactPlayfieldProps) {
  const t = useT();
  const frame = resolutionFrame(state, now);
  const falling = fallingView(state, now);
  const stack = stackView(state, frame);
  const res = frame?.resolution ?? null;
  const max = state.config.wellRows - 1;
  const over = state.phase === 'gameOver';
  const endedByStack = over && !!frame?.endedRun;
  const problem = state.active?.problem ?? (frame?.block && res ? res.problem : null);
  const elapsed = falling ? Math.min(100, Math.max(0, (1 - falling.timeLeft) * 100)) : null;

  let card = null;
  if (endedByStack && res && frame.elapsed >= landingDelayMs(res.kind) + TIMING.STRETCH_MS) {
    card = <GameOverCard compact missed={res.kind === 'correct' ? null : { problem: res.problem, locale }} />;
  } else if (frame?.reveal && res && res.kind !== 'correct' && !endedByStack) {
    card = <AnswerCard compact kind={res.kind} problem={res.problem} locale={locale} />;
  } else if (state.phase === 'levelBanner' && state.pendingLevelChange) {
    card = <LevelBanner compact change={state.pendingLevelChange} tone={levelChangeTone(state.pendingLevelChange, state.checkpoints)} locale={locale} />;
  }

  const sumClasses = [
    'compact-sum',
    falling?.resting && 'compact-sum--resting',
    res && frame?.block && (res.kind === 'correct' ? 'compact-sum--correct' : 'compact-sum--wrong'),
    elapsed !== null && elapsed > 75 && 'compact-sum--late',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`compact-field${isDanger(state) && !over ? ' compact-field--danger' : ''}`}>
      <div className="compact-field__top">
        <PauseButton onPause={onPause} disabled={!canPause} />
        <span className="compact-field__status" aria-hidden="true">
          <span className="compact-field__level">{t('game.levelShort', { level })}</span>
          <span className="compact-field__score">{formatInteger(score)}</span>
        </span>
        <CompactModeSwitch mode={mode} onChoose={onChooseMode} disabled={over} />
      </div>
      <div className="compact-field__main">
        <StackMeter bricks={Math.min(max, stack.layers)} max={max} danger={isDanger(state)} />
        <div className={sumClasses}>
          {problem && (
            <>
              {problem.instruction && <InstructionLine instruction={problem.instruction} locale={locale} className="compact-sum__instruction" />}
              <FittedMath key={problem.id} markup={problem.prompt} locale={locale} basePx={30} minPx={16} fitHeight className="compact-sum__math" />
            </>
          )}
          {elapsed !== null && (
            <svg className="sum-block__timer" aria-hidden="true" focusable="false">
              <rect x="0" y="0" width="100%" height="100%" rx="14" ry="14" pathLength={100} strokeDasharray={`${elapsed.toFixed(2)} 100`} />
            </svg>
          )}
          {frame?.floater != null && res && (
            <span className="compact-sum__floater" aria-hidden="true">
              +{formatInteger(res.scoreDelta.raw)}
            </span>
          )}
        </div>
      </div>
      {card && <div className="compact-field__card">{card}</div>}
    </div>
  );
}
